import React, { useState, useRef, useEffect } from 'react';
import { Send, FileText, Scale, BookOpen, Briefcase, Trash2, AlertCircle, User, LogOut, LogIn, UserPlus, CreditCard } from 'lucide-react';

const API_URL = 'http://localhost:8000';  // Change to your API URL in production

const SwissVATBotWithAuth = () => {
  // Auth state
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  
  // Usage tracking
  const [usage, setUsage] = useState({
    questions_used: 0,
    questions_limit: 5,
    remaining: 5,
    is_guest: true
  });
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [authError, setAuthError] = useState('');
  
  const messagesEndRef = useRef(null);

  // Load session from localStorage
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const storedSessionId = localStorage.getItem('session_id');
    
    if (token) {
      // Verify token and load user
      fetchUserProfile(token);
    } else if (storedSessionId) {
      setSessionId(storedSessionId);
      fetchUsage(storedSessionId);
    } else {
      // Generate new guest session
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      localStorage.setItem('session_id', newSessionId);
    }
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateSessionId = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const fetchUserProfile = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setUsage({
          questions_used: userData.questions_used,
          questions_limit: userData.questions_limit,
          remaining: userData.questions_limit - userData.questions_used,
          is_guest: false
        });
      } else {
        // Token invalid, clear it
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchUsage = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/usage?session_id=${sid}`);
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('auth_token', data.access_token);
        setUser(data.user);
        setUsage({
          questions_used: data.user.questions_used,
          questions_limit: data.user.questions_limit,
          remaining: data.user.questions_limit - data.user.questions_used,
          is_guest: false
        });
        setShowAuthModal(false);
        setEmail('');
        setPassword('');
      } else {
        setAuthError(data.detail || 'Login failed');
      }
    } catch (error) {
      setAuthError('Connection error. Please try again.');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, full_name: fullName })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('auth_token', data.access_token);
        setUser(data.user);
        setUsage({
          questions_used: data.user.questions_used,
          questions_limit: data.user.questions_limit,
          remaining: data.user.questions_limit - data.user.questions_used,
          is_guest: false
        });
        setShowAuthModal(false);
        setEmail('');
        setPassword('');
        setFullName('');
      } else {
        setAuthError(data.detail || 'Signup failed');
      }
    } catch (error) {
      setAuthError('Connection error. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
    
    // Generate new guest session
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    localStorage.setItem('session_id', newSessionId);
    
    setUsage({
      questions_used: 0,
      questions_limit: 5,
      remaining: 5,
      is_guest: true
    });
    
    setMessages([]);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    // Check if limit reached
    if (usage.remaining <= 0) {
      if (usage.is_guest) {
        setShowAuthModal(true);
        setAuthMode('signup');
      }
      return;
    }

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add auth token if user is logged in
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const body = {
        question: input
      };
      
      // Add session_id for guests
      if (!user && sessionId) {
        body.session_id = sessionId;
      }

      const response = await fetch(`${API_URL}/api/ask`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (response.ok) {
        const assistantMessage = {
          role: 'assistant',
          content: data.answer
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Update usage
        setUsage(data.usage);
        
        // Store session_id for guests
        if (data.session_id && !user) {
          setSessionId(data.session_id);
          localStorage.setItem('session_id', data.session_id);
        }
      } else {
        // Handle limit reached
        if (response.status === 403) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `⚠️ ${data.detail}`
          }]);
          
          if (usage.is_guest) {
            setTimeout(() => {
              setShowAuthModal(true);
              setAuthMode('signup');
            }, 1000);
          }
        } else {
          throw new Error(data.detail || 'Error processing question');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Error: ${error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const exampleQuestions = [
    "I'm a consultant with CHF 85,000 turnover. Must I register for VAT?",
    "What VAT rate applies to a restaurant that also sells takeaway?",
    "A French company sells SaaS to Swiss companies. VAT?",
    "Can I deduct VAT on my company car?"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-red-600 p-2 rounded-lg">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">SwissVAT.ai</h1>
                <p className="text-sm text-slate-600">Swiss VAT legal assistant</p>
              </div>
            </div>
            
            {/* User section */}
            <div className="flex items-center gap-3">
              {/* Usage indicator */}
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-700">
                  {usage.remaining} / {usage.questions_limit} questions
                </div>
                <div className="text-xs text-slate-500">
                  {usage.is_guest ? 'Free trial' : `${user?.plan} plan`}
                </div>
              </div>
              
              {/* Auth buttons */}
              {user ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm text-slate-700">{user.email}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowAuthModal(true); setAuthMode('login'); }}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="text-sm">Login</span>
                  </button>
                  <button
                    onClick={() => { setShowAuthModal(true); setAuthMode('signup'); }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="text-sm">Sign Up</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trial banner for guests */}
      {usage.is_guest && usage.remaining <= 3 && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  You have {usage.remaining} question{usage.remaining !== 1 ? 's' : ''} remaining in your free trial
                </span>
              </div>
              <button
                onClick={() => { setShowAuthModal(true); setAuthMode('signup'); }}
                className="px-4 py-1 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700"
              >
                Create account for 20 free questions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-slate-900">Legal Basis</h3>
            </div>
            <p className="text-sm text-slate-600">VAT Act, Ordinance, FTA practice</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-slate-900">Reasoning</h3>
            </div>
            <p className="text-sm text-slate-600">7-step legal structure</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-slate-900">Practical Cases</h3>
            </div>
            <p className="text-sm text-slate-600">Annotated real examples</p>
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden">
          {/* Messages */}
          <div className="h-96 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Ask your VAT question
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Try an example question:
                </p>
                <div className="space-y-2 max-w-2xl mx-auto">
                  {exampleQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(q)}
                      className="w-full text-left px-4 py-2 text-sm bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-lg px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-100 text-slate-900'
                  }`}
                >
                  <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-4 bg-slate-50">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder={usage.remaining > 0 ? "Ask your question about Swiss VAT..." : "Limit reached - Please sign up"}
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-slate-100"
                disabled={loading || usage.remaining <= 0}
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim() || usage.remaining <= 0}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                Send
              </button>
              {messages.length > 0 && (
                <button
                  onClick={() => setMessages([])}
                  className="px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
                  title="New conversation"
                >
                  <Trash2 className="w-5 h-5 text-slate-600" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-4 text-center text-xs text-slate-500">
          <p>Does not replace professional legal advice • {usage.is_guest ? 'Free trial' : 'Registered user'}</p>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                {authMode === 'login' ? 'Login' : 'Create Account'}
              </h2>
              <button
                onClick={() => { setShowAuthModal(false); setAuthError(''); }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {authError}
              </div>
            )}

            <form onSubmit={authMode === 'login' ? handleLogin : handleSignup}>
              {authMode === 'signup' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name (optional)
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="John Doe"
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                {authMode === 'login' ? 'Login' : 'Create Account'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => { setAuthMode(authMode === 'login' ? 'signup' : 'login'); setAuthError(''); }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                {authMode === 'login' 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Login"}
              </button>
            </div>

            {authMode === 'signup' && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ✨ Create an account to get <strong>20 free questions</strong> instead of 5!
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SwissVATBotWithAuth;
